import React, { useEffect, useRef, useState } from 'react';
import {
  BookOpenIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  CheckIcon,
  ChevronRightIcon,
  FolderIcon,
  LinkIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { t } from '../../utils/translations';
import RichText from '../../utils/richText';
import Button, { ButtonIcon, IconButton } from '../ui/Button';

export type TutorialMode = 'welcome' | 'wizard';

interface TutorialWizardProps {
  open: boolean;
  mode: TutorialMode;
  userName: string;
  onStart: () => void;
  onDismiss: () => void;
}

type StepId =
  | 'welcome'
  | 'monthly'
  | 'report'
  | 'notes'
  | 'tablou'
  | 'aprofundeaza'
  | 'road'
  | 'calendar'
  | 'resources';

const STEPS: Array<{ id: StepId; title: string; body: string }> = [
  {
    id: 'welcome',
    title: t.tutorial.steps.welcome.title,
    body: t.tutorial.steps.welcome.body,
  },
  {
    id: 'monthly',
    title: t.tutorial.steps.monthly.title,
    body: t.tutorial.steps.monthly.body,
  },
  {
    id: 'report',
    title: t.tutorial.steps.report.title,
    body: t.tutorial.steps.report.body,
  },
  {
    id: 'notes',
    title: t.tutorial.steps.notes.title,
    body: t.tutorial.steps.notes.body,
  },
  {
    id: 'tablou',
    title: t.tutorial.steps.tablou.title,
    body: t.tutorial.steps.tablou.body,
  },
  {
    id: 'aprofundeaza',
    title: t.tutorial.steps.aprofundeaza.title,
    body: t.tutorial.steps.aprofundeaza.body,
  },
  {
    id: 'road',
    title: t.tutorial.steps.road.title,
    body: t.tutorial.steps.road.body,
  },
  {
    id: 'calendar',
    title: t.tutorial.steps.calendar.title,
    body: t.tutorial.steps.calendar.body,
  },
  {
    id: 'resources',
    title: t.tutorial.steps.resources.title,
    body: t.tutorial.steps.resources.body,
  },
];

const PreviewButton: React.FC<{
  children: React.ReactNode;
  primary?: boolean;
  icon?: ButtonIcon;
}> = ({ children, primary = false, icon: Icon }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11px] font-medium ${
      primary
        ? 'border-brand-600 bg-brand-600 text-white'
        : 'border-slate-200 bg-white text-slate-600'
    }`}
  >
    {Icon && <Icon className="h-3.5 w-3.5" aria-hidden="true" />}
    {children}
  </span>
);

const PreviewPanel: React.FC<{
  label?: string;
  children: React.ReactNode;
  className?: string;
}> = ({ label, children, className = '' }) => (
  <div className={className}>
    {label && (
      <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-slate-400">
        {label}
      </p>
    )}
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">{children}</div>
  </div>
);

const IntroRow: React.FC<{ number: number; children: React.ReactNode }> = ({
  number,
  children,
}) => (
  <div className="flex items-center gap-3">
    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-brand-50 text-xs font-semibold text-brand-700">
      {number}
    </span>
    <p className="text-sm text-slate-700">{children}</p>
  </div>
);

const RoadNode: React.FC<{
  number: number;
  state: 'done' | 'next' | 'future';
}> = ({ number, state }) => (
  <span
    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-semibold ${
      state === 'done'
        ? 'border-brand-600 bg-brand-600 text-white'
        : state === 'next'
          ? 'border-brand-600 bg-white text-brand-700'
          : 'border-slate-200 bg-white text-slate-400'
    }`}
  >
    {number}
  </span>
);

const RoadConnector = () => <span className="h-0.5 min-w-3 flex-1 rounded-full bg-slate-200" />;

const RoadLegend: React.FC<{
  state: 'done' | 'next' | 'future';
  title: string;
  detail: string;
}> = ({ state, title, detail }) => (
  <div className="flex items-center gap-3">
    <RoadNode number={state === 'done' ? 1 : state === 'next' ? 2 : 3} state={state} />
    <div className="min-w-0">
      <p className="text-xs font-semibold text-slate-800">{title}</p>
      <p className="mt-0.5 text-xs leading-5 text-slate-500">{detail}</p>
    </div>
  </div>
);

const WelcomeVisual = () => (
  <PreviewPanel>
    <div className="space-y-3">
      <IntroRow number={1}>{t.tutorial.steps.welcome.reports}</IntroRow>
      <IntroRow number={2}>{t.tutorial.steps.welcome.notes}</IntroRow>
      <IntroRow number={3}>{t.tutorial.steps.welcome.prepare}</IntroRow>
      <IntroRow number={4}>{t.tutorial.steps.welcome.road}</IntroRow>
      <IntroRow number={5}>{t.tutorial.steps.welcome.calendarResources}</IntroRow>
    </div>
  </PreviewPanel>
);

const MonthlyVisual = () => (
  <div className="grid gap-3 sm:grid-cols-2">
    <PreviewPanel label={t.tutorial.steps.monthly.topbar}>
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-xs text-slate-400">Bine ai venit</span>
        <PreviewButton>+ {t.dashboard.raportLunar}</PreviewButton>
      </div>
    </PreviewPanel>
    <PreviewPanel label={t.tutorial.steps.monthly.quickActions}>
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
          <ChartBarIcon className="h-4 w-4" />
        </span>
        <span className="text-xs font-medium text-slate-700">{t.dashboard.raportLunar}</span>
      </div>
    </PreviewPanel>
  </div>
);

const ReportVisual = () => (
  <PreviewPanel label={t.tutorial.steps.report.caseCard}>
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="mr-auto">
          <p className="text-xs font-semibold text-slate-800">Caz activ</p>
          <p className="mt-0.5 text-[11px] text-slate-400">Următoarea: sesiunea 2</p>
        </div>
        <PreviewButton primary>+ Raport</PreviewButton>
        <PreviewButton icon={CalendarDaysIcon}>Programează</PreviewButton>
      </div>
      <div className="border-t border-slate-200 pt-2.5 text-xs text-slate-500">
        {t.tutorial.steps.report.hint}
      </div>
    </div>
  </PreviewPanel>
);

const NotesVisual = () => (
  <PreviewPanel label={t.tutorial.steps.notes.title}>
    <div className="space-y-3">
      <div className="inline-flex w-44 rounded-lg bg-slate-100 p-0.5 text-[11px] font-medium">
        <span className="flex-1 rounded-[7px] bg-white py-1 text-center text-slate-700 shadow-sm">
          {t.meetingNotes.contentTypeAll}
        </span>
        <span className="flex-1 py-1 text-center text-slate-500">
          {t.meetingNotes.contentTypeNote}
        </span>
        <span className="flex-1 py-1 text-center text-slate-500">
          {t.meetingNotes.contentTypeTablou}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <p className="mr-auto text-xs font-semibold text-slate-800">Sesiunea 2</p>
        <PreviewButton primary>+ Notă</PreviewButton>
      </div>
      <div className="border-l-2 border-brand-200 pl-3 text-xs leading-5 text-slate-500">
        {t.tutorial.steps.notes.example}
      </div>
    </div>
  </PreviewPanel>
);

const TabloulVisual = () => (
  <div className="space-y-3">
    <PreviewPanel label={t.tutorial.steps.tablou.layers}>
      <div className="space-y-2.5">
        {[
          t.tablou.situatiaTitle,
          t.tablou.raspunsurileTitle,
          t.tablou.gandurileTitle,
          t.tablou.motiveleTitle,
        ].map((title, index) => (
          <div key={title} className="flex items-center gap-2.5">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-100 text-[10px] font-bold text-brand-700">
              {index + 1}
            </span>
            <span className="text-xs font-medium text-slate-700">{title}</span>
          </div>
        ))}
      </div>
    </PreviewPanel>
    <PreviewPanel>
      <div className="flex items-center gap-2">
        <p className="mr-auto text-xs text-slate-500">Sesiune finalizată.</p>
        <PreviewButton icon={BookOpenIcon}>Vezi tabloul</PreviewButton>
      </div>
    </PreviewPanel>
  </div>
);

const AprofundeazaVisual = () => (
  <div className="space-y-3">
    <PreviewPanel label={t.tutorial.steps.aprofundeaza.caseCard}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-1.5">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
            <BookOpenIcon className="h-4 w-4" />
          </span>
          <span className="text-[10px] text-slate-400">
            {t.tutorial.steps.aprofundeaza.mobileOnly}
          </span>
        </div>
        <PreviewButton icon={BookOpenIcon}>{t.cases.cockpit.deepenCta}</PreviewButton>
      </div>
    </PreviewPanel>
    <PreviewPanel label={t.tutorial.steps.aprofundeaza.preview}>
      <div className="space-y-3">
        <IntroRow number={1}>{t.tutorial.steps.aprofundeaza.subject}</IntroRow>
        <IntroRow number={2}>{t.tutorial.steps.aprofundeaza.tabloulOrReport}</IntroRow>
        <IntroRow number={3}>{t.tutorial.steps.aprofundeaza.practicalTask}</IntroRow>
      </div>
    </PreviewPanel>
  </div>
);

const RoadVisual = () => (
  <div className="space-y-4">
    <PreviewPanel>
      <div className="flex items-center">
        <RoadNode number={0} state="done" />
        <RoadConnector />
        <RoadNode number={1} state="done" />
        <RoadConnector />
        <RoadNode number={2} state="next" />
        <RoadConnector />
        <RoadNode number={3} state="future" />
        <RoadConnector />
        <RoadNode number={4} state="future" />
      </div>
    </PreviewPanel>
    <div className="space-y-3">
      <RoadLegend
        state="done"
        title={t.tutorial.steps.road.done}
        detail={t.tutorial.steps.road.doneDetail}
      />
      <RoadLegend
        state="next"
        title={t.tutorial.steps.road.next}
        detail={t.tutorial.steps.road.nextDetail}
      />
      <RoadLegend
        state="future"
        title={t.tutorial.steps.road.future}
        detail={t.tutorial.steps.road.futureDetail}
      />
    </div>
  </div>
);

const CalendarVisual = () => {
  const days = ['L', 'Ma', 'Mi', 'J', 'V', 'S', 'D'];
  return (
    <div className="space-y-3">
      <PreviewPanel label={t.tutorial.steps.calendar.chooseDay}>
        <div className="grid grid-cols-7 gap-1.5">
          {days.map((day, index) => (
            <span
              key={day}
              className={`flex h-9 items-center justify-center rounded-lg border text-[11px] ${
                index === 3
                  ? 'border-brand-600 bg-brand-600 font-semibold text-white'
                  : 'border-slate-200 bg-white text-slate-500'
              }`}
            >
              {day}
            </span>
          ))}
        </div>
      </PreviewPanel>
      <PreviewPanel label={t.tutorial.steps.calendar.dayPanel}>
        <div className="flex flex-wrap items-center gap-2">
          <span className="mr-auto text-xs text-slate-500">Joi, 12 martie</span>
          <PreviewButton primary icon={CalendarDaysIcon}>
            Programează
          </PreviewButton>
        </div>
      </PreviewPanel>
    </div>
  );
};

const ResourcesVisual = () => (
  <PreviewPanel label={t.tutorial.steps.resources.title}>
    <div className="space-y-1 text-xs">
      <div className="flex items-center gap-2 rounded-lg px-2 py-1.5 font-semibold text-slate-800">
        <ChevronRightIcon className="h-3.5 w-3.5 rotate-90 text-slate-400" />
        <FolderIcon className="h-4 w-4 text-brand-600" />
        {t.tutorial.steps.resources.folder}
      </div>
      {[t.tutorial.steps.resources.firstMeeting, t.tutorial.steps.resources.consent].map((label) => (
        <div key={label} className="ml-8 flex items-center gap-2 rounded-lg px-2 py-1.5 text-slate-600">
          <LinkIcon className="h-4 w-4 text-slate-400" />
          {label}
        </div>
      ))}
      <div className="flex items-center gap-2 rounded-lg px-2 py-1.5 font-semibold text-slate-800">
        <ChevronRightIcon className="h-3.5 w-3.5 text-slate-400" />
        <FolderIcon className="h-4 w-4 text-brand-600" />
        {t.tutorial.steps.resources.family}
      </div>
    </div>
  </PreviewPanel>
);

const StepVisual: React.FC<{ id: StepId }> = ({ id }) => {
  if (id === 'welcome') return <WelcomeVisual />;
  if (id === 'monthly') return <MonthlyVisual />;
  if (id === 'report') return <ReportVisual />;
  if (id === 'notes') return <NotesVisual />;
  if (id === 'tablou') return <TabloulVisual />;
  if (id === 'aprofundeaza') return <AprofundeazaVisual />;
  if (id === 'road') return <RoadVisual />;
  if (id === 'calendar') return <CalendarVisual />;
  return <ResourcesVisual />;
};

const StepTransition: React.FC<{ step: (typeof STEPS)[number] }> = ({ step }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setVisible(true));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  return (
    <div
      className={`transition duration-200 ease-out ${
        visible ? 'translate-y-0 opacity-100' : 'translate-y-1 opacity-0'
      }`}
    >
      <div className="mb-5">
        <h2 className="text-lg font-semibold text-slate-900">{step.title}</h2>
        <RichText text={step.body} className="mt-1.5 text-sm leading-6 text-slate-500" />
      </div>
      <StepVisual id={step.id} />
    </div>
  );
};

const TutorialWizard: React.FC<TutorialWizardProps> = ({
  open,
  mode,
  userName,
  onStart,
  onDismiss,
}) => {
  const [stepIndex, setStepIndex] = useState(0);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    setStepIndex(0);
    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement as HTMLElement | null;
    document.body.style.overflow = 'hidden';
    const timer = window.setTimeout(() => closeRef.current?.focus(), 0);

    return () => {
      window.clearTimeout(timer);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onDismiss]);

  if (!open) return null;

  const firstName = userName.trim().split(/\s+/)[0] || '';
  const welcomeTitle = t.tutorial.welcomeTitle.replace('{name}', firstName);
  const step = STEPS[stepIndex];
  const lastStep = stepIndex === STEPS.length - 1;

  return (
    <div
      className="fixed inset-0 z-[1700] flex h-[100dvh] max-h-[100dvh] justify-center bg-slate-950/45 sm:h-auto sm:max-h-none sm:items-center sm:p-4"
      role="presentation"
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-label={t.tutorial.dialogLabel}
        className="flex h-full max-h-[100dvh] w-full flex-col overflow-hidden bg-white sm:h-auto sm:max-h-[92dvh] sm:max-w-xl sm:rounded-2xl sm:border sm:border-slate-200 sm:shadow-2xl"
      >
        {mode === 'welcome' ? (
          <>
            <div className="flex flex-1 items-start gap-4 px-5 pb-4 pt-[max(1.25rem,env(safe-area-inset-top))] sm:flex-none sm:px-6 sm:pt-6">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
                <BookOpenIcon className="h-6 w-6" />
              </span>
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-semibold text-slate-900">{welcomeTitle}</h2>
                <p className="mt-1.5 text-sm leading-6 text-slate-500">{t.tutorial.welcomeBody}</p>
              </div>
              <IconButton
                ref={closeRef}
                icon={XMarkIcon}
                size="md"
                label={t.common.close}
                onClick={onDismiss}
              />
            </div>
            <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 px-5 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4 sm:px-6 sm:pb-4">
              <Button onClick={onDismiss}>
                {t.tutorial.later}
              </Button>
              <Button variant="primary" icon={BookOpenIcon} onClick={onStart}>
                {t.tutorial.start}
              </Button>
            </div>
          </>
        ) : (
          <>
            <header className="border-b border-slate-100 px-5 pb-4 pt-[max(1rem,env(safe-area-inset-top))] sm:px-6 sm:py-4">
              <div className="flex items-center gap-3">
                <p className="text-sm font-semibold text-slate-900">{t.tutorial.menuLabel}</p>
                <span className="ml-auto text-xs text-slate-400">
                  {t.tutorial.stepProgress
                    .replace('{current}', String(stepIndex + 1))
                    .replace('{total}', String(STEPS.length))}
                </span>
                <IconButton
                  ref={closeRef}
                  icon={XMarkIcon}
                  size="md"
                  label={t.common.close}
                  onClick={onDismiss}
                />
              </div>
              <div className="mt-3 flex items-center gap-1.5" aria-hidden="true">
                {STEPS.map((item, index) => (
                  <span
                    key={item.id}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      index === stepIndex ? 'w-5' : 'w-1.5'
                    } ${index <= stepIndex ? 'bg-brand-600' : 'bg-slate-200'}`}
                  />
                ))}
              </div>
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6 sm:py-6">
              <StepTransition key={step.id} step={step} />
            </div>

            <footer className="flex flex-wrap items-center gap-2 border-t border-slate-100 px-5 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4 sm:px-6 sm:pb-4">
              <Button onClick={onDismiss}>
                {t.tutorial.skip}
              </Button>
              <div className="ml-auto flex gap-2">
                <Button
                  onClick={() => setStepIndex((current) => Math.max(0, current - 1))}
                  disabled={stepIndex === 0}
                >
                  {t.common.back}
                </Button>
                <Button
                  variant="primary"
                  icon={lastStep ? CheckIcon : undefined}
                  onClick={() => {
                    if (lastStep) {
                      onDismiss();
                    } else {
                      setStepIndex((current) => current + 1);
                    }
                  }}
                >
                  {lastStep ? t.common.done : t.tutorial.continue}
                </Button>
              </div>
            </footer>
          </>
        )}
      </section>
    </div>
  );
};

export default TutorialWizard;
